import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  ArrowBack as BackIcon,
  AttachMoney as MoneyIcon,
  DateRange as DateIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Build as GenericIcon,
  Engineering as BodyworkIcon,
  Brush as CleaningIcon,
  Palette as UpholsteryIcon,
  LocalGasStation as FuelIcon,
  Security as InsuranceIcon,
  Settings as AccessoriesIcon,
  QuestionMark as OtherIcon,
  Construction as MaintenanceIcon,
  CheckCircle as StatusIcon,
} from '@mui/icons-material';
import { Expense, ExpenseCategory, EXPENSE_CATEGORIES, expenseAffectsStoreCash, SALE_COMMISSION_TYPE_LABELS } from '../../types';
// @ts-ignore
import { format } from 'date-fns';
import expenseService from '../../services/expenseService';

// Componente para exibir o ícone da categoria de despesa
const ExpenseCategoryIcon = ({ category }: { category: ExpenseCategory }) => {
  switch (category) {
    case 'maintenance':
      return <MaintenanceIcon />;
    case 'mechanics':
      return <GenericIcon />;
    case 'bodywork':
      return <BodyworkIcon />;
    case 'upholstery':
      return <UpholsteryIcon />;
    case 'fuel':
      return <FuelIcon />;
    case 'insurance':
      return <InsuranceIcon />;
    case 'tax':
      return <ReceiptIcon />;
    case 'accessories':
      return <AccessoriesIcon />;
    case 'cleaning':
      return <CleaningIcon />;
    case 'other':
      return <OtherIcon />;
    default:
      return <GenericIcon />;
  }
};

const getCategoryColor = (category: ExpenseCategory): string => {
  switch (category) {
    case 'maintenance': return '#4caf50';
    case 'mechanics': return '#2196f3';
    case 'bodywork': return '#ff9800';
    case 'upholstery': return '#9c27b0';
    case 'fuel': return '#f44336';
    case 'insurance': return '#607d8b';
    case 'tax': return '#795548';
    case 'accessories': return '#00bcd4';
    case 'cleaning': return '#8bc34a';
    case 'other': return '#9e9e9e';
    default: return '#9e9e9e';
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'paid':
      return '#4caf50'; // Verde
    case 'pending':
      return '#ff9800'; // Laranja
    case 'cancelled':
      return '#f44336'; // Vermelho
    default:
      return '#9e9e9e'; // Cinza
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'paid':
      return 'Pago';
    case 'completed':
      return 'Concluída';
    case 'pending':
      return 'Pendente';
    case 'cancelled':
      return 'Cancelado';
    default:
      return 'Desconhecido';
  }
};

const ExpenseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expense, setExpense] = useState<Expense | null>(null);

  useEffect(() => {
    const fetchExpenseDetails = async () => {
      try {
        setLoading(true);

        // Usar o serviço real para buscar os detalhes da despesa
        const data = await expenseService.getExpense(parseInt(id!));
        setExpense(data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar detalhes da despesa:', error);
        setError('Não foi possível carregar os detalhes da despesa. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchExpenseDetails();
  }, [id]);

  const handleEditExpense = () => {
    navigate(`/despesas/editar/${id}`);
  };

  const handleBackToList = () => {
    navigate('/despesas');
  };

  // Formatar valores monetários
  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'; // Retorna um hífen se a data não existir

    try {
      const cleanDateString = dateString.split('T')[0];  // Vai pegar apenas '2025-05-01'
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return '-'; // Retorna um hífen em caso de erro
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  if (!expense) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
          Despesa não encontrada.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackToList}
            sx={{ mr: 2 }}
            color="inherit"
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Detalhes da Despesa
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            onClick={handleEditExpense}
            sx={{ borderRadius: '8px' }}
          >
            Editar Despesa
          </Button>
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mb: 4,
          }}
        >
          {/* Cabeçalho com categoria e valor */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: getCategoryColor(expense.category),
                  width: 56,
                  height: 56,
                  mr: 2,
                }}
              >
                <ExpenseCategoryIcon category={expense.category} />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                  {EXPENSE_CATEGORIES[expense.category]}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {expense.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h4" component="p" color="error" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(expense.amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data: {formatDate(expense.expense_date)}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Chip
                  size="small"
                  label={
                    expenseAffectsStoreCash(expense)
                      ? 'Desconta do caixa da loja'
                      : 'Apenas lucro do veículo (não desconta do caixa)'
                  }
                  color={expenseAffectsStoreCash(expense) ? 'default' : 'info'}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Informações do veículo */}
          <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CarIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Informações do Veículo
          </Typography>

   {/* Informações do veículo */}
          <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CarIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Informações do vendedor
          </Typography>
          <Card sx={{ mb: 3, borderRadius: '8px', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                   Nome do vendedor
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {expense.seller?.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo de comissão do vendedor
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {SALE_COMMISSION_TYPE_LABELS[expense?.commission_type as keyof typeof SALE_COMMISSION_TYPE_LABELS || 'fixed']}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Valor 
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {expense.comission_value} {expense.commission_type === 'fixed' ? 'R$' : '%'}
                  </Typography>
                </Grid>
              
              </Grid>
            </CardContent>
          </Card>

          <Divider sx={{ my: 3 }} />

          {/* Detalhes da despesa */}
          <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
            Detalhes da Despesa
          </Typography>

          <Grid container spacing={3}>
        {/* Coluna da Esquerda */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Categoria
            </Typography>
            <Chip
              icon={<ExpenseCategoryIcon category={expense.category} />}
              label={EXPENSE_CATEGORIES[expense.category]}
              sx={{
                bgcolor: getCategoryColor(expense.category) + '20',
                color: getCategoryColor(expense.category),
                fontWeight: 'medium',
                mt: 0.5,
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Chip
              icon={<StatusIcon />}
              label={getStatusLabel(expense.status)}
              sx={{
                bgcolor: getStatusColor(expense.status) + '20',
                color: getStatusColor(expense.status),
                fontWeight: 'medium',
                mt: 0.5,
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Data
            </Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <DateIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
              {formatDate(expense.expense_date)}
            </Typography>
          </Box>
        </Grid>

        {/* Coluna da Direita */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Valor
            </Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <MoneyIcon sx={{ mr: 1, fontSize: 20, color: 'error.main' }} />
              {formatCurrency(expense.amount)}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Comprovante
            </Typography>
            {expense.url_documento ? (
              <Button
                startIcon={<ReceiptIcon />}
                variant="outlined"
                size="small"
                href={expense.url_documento}
                target="_blank"
                sx={{ mt: 0.5 }}
              >
                Ver Comprovante
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Nenhum comprovante anexado
              </Typography>
            )}
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Descrição
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {expense.description}
            </Typography>
          </Box>
        </Grid>

        {/* Exibindo Observações se houver */}
        {expense.notes && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Observações
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {expense.notes}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>


        </Paper>
      </div>
    </Fade>
  );
};

export default ExpenseDetails; 